import React from 'react';
import cx from 'classnames';

import { MDText } from 'i18n-react';

import { CameraIcon, IdIcon, PaperStackIcon, TickCircleIcon, UploadIcon } from '@motorway/mw-highway-code';

import { DOCUMENT_KINDS } from '../../../DocumentCapture.helpers';
import { ExampleDocuments } from '../../ExampleDocuments/ExampleDocuments';
import { EXAMPLE_DOCUMENTS } from '../../ExampleDocuments/ExampleDocuments.helpers';
import LocalTexts from '../../UploadDocuments.texts.json';

import styles from './UploadDocumentsVerification.scss';

const LocalT = new MDText(LocalTexts.uploadDocumentsVerification);

type IconComponent = React.ComponentType<{ filled?: boolean }>;

type StepConfig = {
  content: React.ReactNode;
  icon: IconComponent;
  iconClassName?: string;
  iconFilled?: boolean;
};

const makeStep = ({ content, icon: IconCmp, iconClassName, iconFilled = true }: StepConfig, id: number) => ({
  content: (
    <>
      <div className={cx(styles.stepIcon, iconClassName)}>
        <IconCmp filled={iconFilled} />
      </div>
      <p data-thc-style-override>{content}</p>
    </>
  ),
  id,
});

const exampleDocLink = (
  kind: keyof typeof EXAMPLE_DOCUMENTS,
  actionLinkKey: string,
  titleKey: string,
  descriptionKey: string,
  checklist?: string[],
) => (
  <span className={styles.exampleDocLink}>
    <ExampleDocuments
      {...EXAMPLE_DOCUMENTS[kind] as Record<string, unknown>}
      actionLink={LocalT.translate(actionLinkKey) as string}
      documentTitle={LocalT.translate(titleKey) as string}
      modalDescription={LocalT.translate(descriptionKey) as string}
      {...(checklist ? { modalSummaryList: checklist.map((text) => LocalT.translate(text)) } : {})}
    />
  </span>
);

const drivingLicence = () => exampleDocLink(
  DOCUMENT_KINDS.DRIVING_LICENCE as keyof typeof EXAMPLE_DOCUMENTS,
  'steps.drivingLicence',
  'steps.drivingLicenceModalTitle',
  'steps.drivingLicenceModalDescription',
);

const v5c = () => exampleDocLink(
  DOCUMENT_KINDS.V5 as keyof typeof EXAMPLE_DOCUMENTS,
  'steps.v5c',
  'steps.v5c',
  'steps.v5cModalDescription',
);

const financeSettlementLetter = () => exampleDocLink(
  DOCUMENT_KINDS.FINANCE_SETTLEMENT_LETTER as keyof typeof EXAMPLE_DOCUMENTS,
  'steps.financeSettlementLetter',
  'steps.financeSettlementLetter',
  'steps.financeSettlementLetterModalDescription',
  LocalTexts.uploadDocumentsVerification.steps.financeSettlementLetterModalChecklist,
);

const fslStep = makeStep({
  content: LocalT.translate('steps.uploadYourDocument', { document: financeSettlementLetter() }),
  icon: UploadIcon,
  iconClassName: styles.financeSettlementLetterIcon,
}, 4);

// Used when the seller is the registered keeper. Keeps the existing three-step
// layout (driver's licence, selfie, V5C) with an optional finance step.
export const getSellerKeeperSteps = ({ showFSLStep = false }: { showFSLStep?: boolean }) => [
  makeStep({
    content: LocalT.translate('steps.snapYourDrivingLicence', { drivingLicence: drivingLicence() }),
    icon: IdIcon,
  }, 1),
  makeStep({
    content: LocalT.translate('steps.takeASelfie'),
    icon: CameraIcon,
    iconFilled: false,
  }, 2),
  makeStep({
    content: LocalT.translate('steps.uploadYourDocument', { document: v5c() }),
    icon: PaperStackIcon,
  }, 3),
  ...(showFSLStep ? [fslStep] : []),
];

// Step 1 of the two-step layout (family/friend keeper): what the registered
// keeper does to authorise the sale.
export const getKeeperSteps = () => [
  makeStep({
    content: LocalT.translate('steps.keeperStep.takePhotoOfLicence', { drivingLicence: drivingLicence() }),
    icon: IdIcon,
  }, 1),
  makeStep({
    content: LocalT.translate('steps.keeperStep.confirmAuthorisation'),
    icon: TickCircleIcon,
    iconClassName: styles.tickCircleIcon,
    iconFilled: false,
  }, 2),
];

// Step 2 of the two-step layout (family/friend keeper): what the seller does
// to verify themselves.
export const getSellerSteps = ({ showFSLStep = false }: { showFSLStep?: boolean }) => [
  makeStep({
    content: LocalT.translate('steps.sellerStep.takePhotoOfLicence', { drivingLicence: drivingLicence() }),
    icon: IdIcon,
  }, 1),
  makeStep({
    content: LocalT.translate('steps.sellerStep.uploadV5c', { v5c: v5c() }),
    icon: PaperStackIcon,
  }, 2),
  ...(showFSLStep ? [{ ...fslStep, id: 3 }] : []),
];
